import { useState, useEffect, useContext } from 'react';
import PageContext from './PageContext';
import { TableCell, Checkbox, Box } from '@mui/material';
import { ReactComponent as SelectedIcon } from '../../svgs/selected.svg';
import { ReactComponent as UnselectedIcon } from '../../svgs/unselected.svg';
import ChatIcon from '../../svgs/chat-icon.svg';
import AuditTrailIcon from '../../svgs/audit-trail-icon.svg';
import PageTooltip from './PageTooltip';
import displayDateTime from '../functions/displayDateTime';

const ClientProfileWorkflowRow = ({ row, count, tableHeader, stickyEnabled, rowIndex, selectableRows }) => {
    const {
        checkAll,
        setCheckAll,
        workflowActionCheckedList,
        setWorkflowActionCheckedList,
        checkBoxChange,
        workflowStatus,
        setCommentModal,
        setAuditModal,
        setAuditRow,
        fieldDisplayNameMap,
        payoutMethodMap,
        achAccountTypeMap,
    } = useContext(PageContext);

    const [checked, setChecked] = useState(false);
    const [showCheckbox, setShowCheckbox] = useState(false);

    // event handler for comment icon click
    const onCommentClick = (e) => {
        e.stopPropagation();
        setAuditRow(row);
        setCommentModal(true);
    };

    // event handler for audit trail icon click
    const onAuditClick = (e) => {
        e.stopPropagation();
        setAuditRow(row);
        setAuditModal(true);
    };

    // event handler for checkbox click
    const onCheckboxClick = () => {
        if (!checked) {
            setChecked(true);
            setWorkflowActionCheckedList(prev => [...prev, row]);
        } else {
            setChecked(false);
            let index = workflowActionCheckedList.indexOf(row);
            setWorkflowActionCheckedList(prev => [...prev.slice(0, index), ...prev.slice(index + 1)]);
            if (checkAll) {
                setCheckAll(false);
            }
        }
    };

    // show status color coded badge
    const showStatus = (value) => {
        let color = '#4472C4';
        let textColor = 'white';
        if (value === 'Pending') { color = '#FFC700'; textColor = 'black'; }
        else if (value === 'Final Review') { color = '#00A6CA'; textColor = 'white'; }
        else if (value === 'Accepted') { color = '#008A00'; textColor = 'white'; }
        else if (value === 'Rejected') { color = '#EB0000'; textColor = 'white'; }
        return (
            <div className="d-flex justify-content-start align-items-center gap-2">
                <Box sx={{ minWidth: 12, height: 12, bgcolor: color, borderRadius: 100 }} />
                <div>{value}</div>
            </div>
        );
    };

    // parse pipe-delimited value string into object
    // e.g. "payoutmethodid: 1 | achaccountnumber: 1234123 | ..."
    const parseValues = (valueStr) => {
        if (!valueStr) return {};
        let obj = {};
        valueStr.split(' | ').forEach(part => {
            let colonIdx = part.indexOf(':');
            if (colonIdx !== -1) {
                let key = part.slice(0, colonIdx).trim();
                let val = part.slice(colonIdx + 1).trim();
                obj[key] = val === 'null' ? null : val;
            }
        });
        return obj;
    };

    // helper to get display value for a field
    const getFieldDisplayValue = (fieldKey, fieldValue) => {
        // if payoutmethodid, use payout method description instead of ID
        if (fieldKey === 'payoutmethodid' && payoutMethodMap[fieldValue]) {
            return payoutMethodMap[fieldValue];
        }
        // if achaccounttype, use account type description instead of ID
        if (fieldKey === 'achaccounttype' && achAccountTypeMap[fieldValue]) {
            return achAccountTypeMap[fieldValue];
        }
        return fieldValue;
    };

    // display only the changed fields from oldvalue/newvalue using the field column
    // if only 1 field changed show just the value, if multiple show "field: value" pairs
    // uses fieldDisplayNameMap to show display names instead of column IDs
    const getDisplayValue = (valueStr) => {
        if (!valueStr || !row['field']) return valueStr ?? 'None';
        let parsed = parseValues(valueStr);
        if (row['field'] === 'all') {
            // show all non-null fields for 'add' workflows
            let fields = Object.keys(parsed)
                .filter(k => parsed[k] !== null && parsed[k] !== undefined && parsed[k] !== 'null' && parsed[k] !== '')
                .map(k => {
                    let displayName = fieldDisplayNameMap[k] || k;
                    let displayValue = getFieldDisplayValue(k, parsed[k]);
                    return `${displayName}: ${displayValue}`;
                });
            return fields.length > 0 ? fields.join(' | ') : 'None';
        }
        let changedFields = row['field'].split(', ').map(f => f.trim());
        if (changedFields.length === 1) {
            // single field changed — show just the value
            let val = parsed[changedFields[0]];
            if (val === null || val === undefined) return 'null';
            return getFieldDisplayValue(changedFields[0], val);
        }
        // multiple fields changed — show "field: value" pairs for changed fields only
        return changedFields.map(f => {
            let displayName = fieldDisplayNameMap[f] || f;
            let displayValue = getFieldDisplayValue(f, parsed[f] ?? 'null');
            return `${displayName}: ${displayValue}`;
        }).join(' | ');
    };

    // convert field column to display names
    // e.g. "payoutmethodid, achaccountnumber" -> "Payout Method, ACH Account Number"
    // if field is 'all', display as "All"
    const getDisplayFieldName = (fieldStr) => {
        if (!fieldStr) return fieldStr;
        if (fieldStr === 'all') return 'All';
        return fieldStr.split(', ').map(f => {
            let displayName = fieldDisplayNameMap[f.trim()] || f.trim();
            return displayName;
        }).join(', ');
    };

    const getRowClassName = (columnOrder) => {
        return stickyEnabled && columnOrder === 0 ? 'table-cell-sticky-row' : '';
    };

    // sync checkbox with checkAll toggle
    useEffect(() => {
        if (row['status'] === workflowStatus) {
            setChecked(checkAll);
        } else {
            setChecked(false);
        }
    }, [checkBoxChange]);

    // sync checkbox with workflowActionCheckedList
    useEffect(() => {
        setChecked(workflowActionCheckedList.includes(row) && row['status'] === workflowStatus);
    }, [workflowActionCheckedList]);

    // show checkbox only if:
    // 1. workflowStatus is not 'All'
    // 2. row status matches workflowStatus
    // 3. user has the required role for this status (no Admin/Tech bypass — matches Rebate Summary exactly):
    //    - Pending status requires CCRM role ('5')
    //    - Final Review status requires Reviewer role ('3')
    useEffect(() => {
        let hasRole = false;
        if (workflowStatus === 'Pending' && row['userroleid'] === process.env.REACT_APP_SUBMITTER_ROLE_ID) {
            hasRole = true;
        } else if (workflowStatus === 'Final Review' && row['userroleid'] === process.env.REACT_APP_REVIEWER_ROLE_ID) {
            hasRole = true;
        }
        setShowCheckbox(workflowStatus !== 'All' && row['status'] === workflowStatus && hasRole);
    }, [workflowStatus, row]);

    return (
        <>
            {tableHeader.map(el =>
                el['columnname'] === 'status' ?
                    <TableCell key={el['columnname']} className={getRowClassName(el['columnorder'])}>
                        {showStatus(row[el['columnname']])}
                    </TableCell>
                : el['columnname'] === 'blank' ?
                    <TableCell key={el['columnname']} className={getRowClassName(el['columnorder'])} align='center'>
                        <PageTooltip title='Comments'>
                            <img src={ChatIcon} className='data-table-icon me-2' onClick={onCommentClick} alt="comments" />
                        </PageTooltip>
                        <PageTooltip title='Audit Trail'>
                            <img src={AuditTrailIcon} className='data-table-icon' onClick={onAuditClick} alt="audit trail" />
                        </PageTooltip>
                    </TableCell>
                : el['columnname'] === 'checkbox' ?
                    <TableCell key={el['columnname']} sx={{ backgroundColor: rowIndex % 2 !== 0 ? '#E6EDF1' : '#F7F9FA' }} className={getRowClassName(el['columnorder'])}>
                        {showCheckbox ?
                            <Checkbox
                                sx={{ padding: 0 }}
                                checked={checked}
                                onChange={onCheckboxClick}
                                checkedIcon={<SelectedIcon />}
                                icon={<UnselectedIcon />}
                            />
                        : <></>}
                    </TableCell>
                : el['columnname'] === 'updatetimestamp' ?
                    <TableCell key={el['columnname']} className={getRowClassName(el['columnorder'])}>
                        {row[el['columnname']] ? displayDateTime(row[el['columnname']]) : 'None'}
                    </TableCell>
                : el['columnname'] === 'oldvalue' || el['columnname'] === 'newvalue' ?
                    <TableCell key={el['columnname']} className={getRowClassName(el['columnorder'])}>
                        {getDisplayValue(row[el['columnname']])}
                    </TableCell>
                : el['columnname'] === 'field' ?
                    <TableCell key={el['columnname']} className={getRowClassName(el['columnorder'])}>
                        {getDisplayFieldName(row[el['columnname']])}
                    </TableCell>
                : row[el['columnname']] == null ?
                    <TableCell key={el['columnname']} className={getRowClassName(el['columnorder'])}>None</TableCell>
                : <TableCell key={el['columnname']} className={getRowClassName(el['columnorder'])}>{row[el['columnname']]}</TableCell>
            )}
        </>
    );
};


export default ClientProfileWorkflowRow;
